import { SortIcon } from "@/components/icon";
import { Text } from "@/components/text";
import { getStatusMap } from "@/utilities";
import { useDroppable } from "@dnd-kit/core";
import { Button, Dropdown, Space, Tag, theme } from "antd";
import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import KanbanItem from "./item";
import { QuoteCard } from "@/components/quotes/QuoteCard";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

type Props = {
  id: string;
  title: string;
  count: number;
  quotes?: any[];
  isDragging?: boolean;
  setIsDragging: any;
  setActiveId: any;
  setDraggedQuote: any;
  activeId: string | null;
};
type SortField = "createdAt" | "updatedAt" | "name" | "total" | null;
type SortOrder = "asc" | "desc" | null;

const KanbanColumn: React.FC<Props> = React.memo(
  ({
    id,
    title,
    count,
    quotes,
    setIsDragging,
    setActiveId,
    setDraggedQuote,
    activeId,
  }) => {
    const { isOver, setNodeRef, active } = useDroppable({ id });
    const { token } = theme.useToken();
    const { t } = useTranslation();

    const [sortConfig, setSortConfig] = useState<{
      field: SortField;
      order: SortOrder;
    }>({ field: null, order: null });

    const [isExpanded, setIsExpanded] = useState(false);

    const getSortMenuItems = () => {
      const fields = [
        { key: "createdAt", label: "Fecha de creación" },
        { key: "updatedAt", label: "Fecha de actualización" },
        { key: "name", label: "Nombre" },
        { key: "total", label: "Total" },
      ];
      return fields.map(({ key, label }) => ({
        key,
        label: (
          <Space>
            {label}
            {sortConfig.field === key &&
              (sortConfig.order === "asc"
                ? "↑"
                : sortConfig.order === "desc"
                ? "↓"
                : "")}
          </Space>
        ),
      }));
    };

    const sortedQuotes = useMemo(() => {
      if (!quotes) return [];
      if (!sortConfig.field || !sortConfig.order) return quotes;
      return [...quotes].sort((a, b) => {
        let valueA, valueB;
        switch (sortConfig.field) {
          case "createdAt":
            valueA = new Date(a.createdAt).getTime();
            valueB = new Date(b.createdAt).getTime();
            break;
          case "updatedAt":
            valueA = new Date(a.updatedAt).getTime();
            valueB = new Date(b.updatedAt).getTime();
            break;
          case "name":
            valueA = a.name.toLowerCase();
            valueB = b.name.toLowerCase();
            break;
          case "total":
            valueA = a.quoteSellers?.[0]?.total || 0;
            valueB = b.quoteSellers?.[0]?.total || 0;
            break;
          default:
            return 0;
        }
        return sortConfig.order === "asc"
          ? valueA > valueB
            ? 1
            : -1
          : valueA < valueB
          ? 1
          : -1;
      });
    }, [quotes, sortConfig]);

    const visibleQuotes = useMemo(
      () => (isExpanded ? sortedQuotes : sortedQuotes.slice(0, 20)),
      [isExpanded, sortedQuotes],
    );

    const statusMap = getStatusMap(t as any);
    const tagData =
      statusMap[title as keyof typeof statusMap] || statusMap.unknown;

    return (
      <div
        ref={setNodeRef}
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: token.colorBorderSecondary,
        }}
      >
        <div
          style={{
            padding: "12px",
            minWidth: 356,
            backgroundColor: token.colorBgContainer,
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <Space>
              <Text
                ellipsis={{ tooltip: title }}
                size="xs"
                strong
                style={{
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                <Tag style={tagData.style}>{tagData.label}</Tag>
              </Text>
            </Space>
            <Space>
              <Dropdown
                arrow
                placement="bottomRight"
                overlayStyle={{ minWidth: 200 }}
                trigger={["click"]}
                menu={{
                  items: getSortMenuItems(),
                  selectable: true,
                  selectedKeys: sortConfig.field ? [sortConfig.field] : [],
                  onClick: ({ key }) => {
                    const typedKey = key as SortField; // <-- aquí el fix tipado
                    setSortConfig((prev) => {
                      if (prev.field !== typedKey) {
                        return { field: typedKey, order: "desc" as SortOrder };
                      } else {
                        if (prev.order === "desc") {
                          return { field: typedKey, order: "asc" as SortOrder };
                        } else if (prev.order === "asc") {
                          return { field: null, order: null };
                        } else {
                          return {
                            field: typedKey,
                            order: "desc" as SortOrder,
                          };
                        }
                      }
                    });
                  },
                }}
              >
                <Button shape="circle" icon={<SortIcon />} />
              </Dropdown>
            </Space>
          </Space>
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Text strong>
              {sortedQuotes
                .reduce(
                  (sum, q) =>
                    sum +
                    (Array.isArray(q.quoteSellers)
                      ? q.quoteSellers.reduce(
                          (sSum: number, s: any) => sSum + (s.total || 0),
                          0,
                        )
                      : 0),
                  0,
                )
                .toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}
            </Text>
            <span>
              {count || 0} {t("quotes").toLocaleLowerCase()}
            </span>
          </Space>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: active ? "unset" : "auto",
            border: "2px dashed transparent",
            borderColor: isOver ? "#000040" : "transparent",
            borderRadius: "4px",
          }}
        >
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {visibleQuotes.map((quote) => (
              <KanbanItem
                key={quote.id}
                id={quote.id}
                data={{ ...quote, state: id }}
                setIsDragging={setIsDragging}
                setActiveId={setActiveId}
                setDraggedQuote={setDraggedQuote}
                quote={quote}
                activeId={activeId}
              >
                <QuoteCard {...quote} />
              </KanbanItem>
            ))}

            {sortedQuotes.length > 10 && !isExpanded && (
              <div style={{ textAlign: "center", marginTop: "8px" }}>
                <Button
                  size="small"
                  shape="round"
                  onClick={() => setIsExpanded(true)}
                >
                  {t("quotes.viewMore")} <DownOutlined />
                </Button>
              </div>
            )}

            {isExpanded && (
              <div style={{ textAlign: "center", marginTop: "8px" }}>
                <Button
                  size="small"
                  shape="round"
                  onClick={() => setIsExpanded(false)}
                >
                  {t("quotes.viewLess")} <UpOutlined />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default KanbanColumn;
